import { Type, type Static } from "@sinclair/typebox";
import {
  PlanningItemCreateSchema,
  PlanningItemUpdateSchema,
  PlanningItemDeleteQuerySchema,
  PlanningFeasibleUpdateSchema,
} from "./operations.js";
import { InspectionSaveSchema } from "./lifeline-inspections.js";
const strict = { additionalProperties: false } as const;
export const AssistantPlanningCreateSchema = Type.Object(
  {
    ...Type.Omit(PlanningItemCreateSchema, ["operationId"]).properties,
    jobVersion: Type.Optional(Type.Integer({ minimum: 1 })),
  },
  strict,
);
export const AssistantPlanningUpdateSchema = Type.Omit(
  PlanningItemUpdateSchema,
  ["operationId"],
);
export const AssistantPlanningDeleteSchema = Type.Omit(
  PlanningItemDeleteQuerySchema,
  ["operationId"],
);
export const AssistantPlanningFeasibleSchema = Type.Omit(
  PlanningFeasibleUpdateSchema,
  ["operationId"],
);
export const AssistantPlanningWindowSchema = Type.Object(
  {
    from: Type.String({ format: "date" }),
    to: Type.String({ format: "date" }),
  },
  strict,
);
export const AssistantInspectionFormSchema = Type.Object(
  {
    ...InspectionSaveSchema.properties.form.properties,
    answers: Type.Record(
      Type.String({ pattern: "^s[1-7]-[1-7]$" }),
      Type.Union([
        Type.Literal("yes"),
        Type.Literal("no"),
        Type.Literal("na"),
        Type.Literal("nv"),
      ]),
      { maxProperties: 35 },
    ),
    temperature: Type.Unsafe<number | null>({
      type: ["number", "null"],
      minimum: -80,
      maximum: 80,
    }),
    outcome: Type.Unsafe<
      "usable" | "restricted" | "specialist" | "out-of-service" | null
    >({
      type: ["string", "null"],
      enum: ["usable", "restricted", "specialist", "out-of-service", null],
    }),
  },
  strict,
);
export const AssistantInspectionChangesSchema = Type.Partial(
  Type.Omit(AssistantInspectionFormSchema, ["signature"]),
  { ...strict, minProperties: 1 },
);
export const AssistantInspectionPreviewSchema = Type.Object(
  {
    checklistId: Type.Optional(Type.String({ format: "uuid" })),
    revision: Type.Integer({ minimum: 0 }),
    status: Type.Union([Type.Literal("draft"), Type.Literal("completed")]),
    changes: AssistantInspectionChangesSchema,
  },
  strict,
);
export const AssistantInspectionListSchema = Type.Object(
  {
    offset: Type.Optional(
      Type.Integer({ minimum: 0, maximum: 100000, default: 0 }),
    ),
    limit: Type.Optional(Type.Integer({ minimum: 1, maximum: 10, default: 5 })),
  },
  strict,
);
export type AssistantPlanningCreate = Static<
  typeof AssistantPlanningCreateSchema
>;
export type AssistantInspectionPreview = Static<
  typeof AssistantInspectionPreviewSchema
>;
export type AssistantOperationsChange =
  | {
      kind: "planning.create";
      id: string;
      input: AssistantPlanningCreate;
      expected: string;
    }
  | {
      kind: "planning.update";
      id: string;
      input: Static<typeof AssistantPlanningUpdateSchema>;
    }
  | {
      kind: "planning.delete";
      id: string;
      input: Static<typeof AssistantPlanningDeleteSchema>;
    }
  | {
      kind: "planning.feasible";
      id: string;
      input: Static<typeof AssistantPlanningFeasibleSchema>;
      expected: string;
    }
  | {
      kind: "inspection.save";
      id: string;
      input: Omit<
        Static<typeof InspectionSaveSchema>,
        "operationId" | "form"
      > & { form: Static<typeof AssistantInspectionFormSchema> };
    };
